// El sonido de la web, sintetizado con Web Audio: sin ficheros (nada que descargar, ni licencias), y el volumen y la
// forma de cada sonido viven en el código. Un solo AudioContext para toda la web (el «ding» de /home,
// notification-sound.ts, y los avisos de Santi de la oficina, features/oficina/avisos/sonidos.ts).
//
// Los navegadores no dejan sonar antes del primer gesto (clic, tecla) de la página: un AudioContext creado antes nace
// «suspendido» y su `resume()` se queda esperando (no falla) hasta que hay un gesto. Por eso: (1) al primer clic o
// tecla se crea y se reanuda (`desbloquearAudioAlPrimerGesto`), y desde entonces suena también con la pestaña de
// fondo; (2) tocar nunca espera más de un momento a ese `resume()` ni lanza: si el navegador no deja, no suena (y
// `tocarNotas` lo dice con false).
// 04-11 (Pau: «suenan muy flojito»): cada vez que suena, sus notas pasan por una cadena propia — un volumen maestro
// (`opciones.volumen`, 0-1: el de «Avisos y sonido») y un compresor — antes de los altavoces: las notas pueden ir fuertes
// (un aviso de Santi a tope ronda el de un aviso de Windows o del Mac) sin que, al sumarse, saturen ni distorsionen. El
// «ding» de /home va sin compresor (`comprimir: false`: el de Web Audio sube un poco todo lo que pasa por él) y suena
// exactamente igual que antes.

export interface Nota {
  /** Hz al empezar. */
  frecuencia: number;
  /** Hz al terminar (un deslizamiento, p. ej. una gota); sin él, la nota no cambia. */
  hasta?: number;
  /** Segundos desde el principio del sonido. */
  inicio: number;
  /** Segundos hasta apagarse. */
  duracion: number;
  /** 0-1 (en la práctica ≤ 0,15: son avisos, no alarmas). */
  volumen: number;
  /** Segundos que tarda en llegar al volumen (suave = más). Por defecto, 10 ms. */
  ataque?: number;
  forma?: OscillatorType;
}

/** Lo más que se espera a que el navegador reanude el audio antes de rendirse (sin gesto, no lo reanuda nunca). */
export const ESPERA_REANUDAR_MS = 300;

let ctx: AudioContext | null = null;

export function contextoDeAudio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/** Solo para las pruebas: olvida el contexto (cada prueba trae el suyo). */
export function olvidarContextoDeAudio(): void {
  ctx = null;
}

// Leído siempre de nuevo: `resume()` lo cambia por debajo.
const estado = (ac: AudioContext): string => ac.state;

/** El audio, listo para sonar: true si está en marcha (o se ha podido reanudar a tiempo). Nunca lanza. */
async function enMarcha(ac: AudioContext): Promise<boolean> {
  if (estado(ac) === 'running') return true;
  try {
    await Promise.race([ac.resume(), new Promise((r) => setTimeout(r, ESPERA_REANUDAR_MS))]);
  } catch {
    return false;
  }
  return estado(ac) === 'running';
}

/**
 * El compresor de cada sonido: doma los picos (umbral −6 dB, relación 12:1, ataque de 2 ms) y, con la ganancia de
 * compensación que Web Audio pone sola, deja los picos de las notas sumadas en ~0,8 a la salida, sin saturar.
 */
export const COMPRESOR = { umbral: -6, rodilla: 4, relacion: 12, ataque: 0.002, liberacion: 0.15 } as const;
/**
 * Un compresor recién creado se come los primeros ~50 ms (medido en Chromium: una gota que empieza al momento sale a
 * 0,32 de pico; 50 ms después, a 0,84). Con compresor, las notas empiezan esto después de montar la cadena (no se nota).
 */
export const ADELANTO_COMPRESOR_S = 0.06;

export interface OpcionesTocar {
  /** Volumen maestro, 0-1 (por defecto 1). 0 = no suena (y no se crea nada). */
  volumen?: number;
  /** Con compresor (por defecto). false: como antes, solo el volumen (el «ding» de /home). */
  comprimir?: boolean;
}

/** Toca unas notas. true si sonaron (o no había nada que tocar); false si no hay audio o el navegador no deja todavía. */
export async function tocarNotas(notas: readonly Nota[], opciones: OpcionesTocar = {}): Promise<boolean> {
  const volumen = Math.min(1, Math.max(0, opciones.volumen ?? 1));
  if (!notas.length || volumen === 0) return true;
  const ac = contextoDeAudio();
  if (!ac || !(await enMarcha(ac))) return false;
  try {
    const comprimir = opciones.comprimir !== false;
    const t0 = ac.currentTime + (comprimir ? ADELANTO_COMPRESOR_S : 0.01);
    // La cadena de este sonido: notas → volumen maestro → compresor → altavoces.
    const maestro = ac.createGain();
    maestro.gain.setValueAtTime(volumen, t0);
    if (!comprimir) maestro.connect(ac.destination);
    else {
      const compresor = ac.createDynamicsCompressor();
      compresor.threshold.setValueAtTime(COMPRESOR.umbral, t0);
      compresor.knee.setValueAtTime(COMPRESOR.rodilla, t0);
      compresor.ratio.setValueAtTime(COMPRESOR.relacion, t0);
      compresor.attack.setValueAtTime(COMPRESOR.ataque, t0);
      compresor.release.setValueAtTime(COMPRESOR.liberacion, t0);
      maestro.connect(compresor).connect(ac.destination);
    }
    for (const n of notas) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      const a = t0 + n.inicio;
      const fin = a + n.duracion;
      osc.type = n.forma ?? 'sine';
      osc.frequency.setValueAtTime(n.frecuencia, a);
      if (n.hasta) osc.frequency.exponentialRampToValueAtTime(n.hasta, a + n.duracion * 0.6);
      gain.gain.setValueAtTime(0.0001, a);
      gain.gain.exponentialRampToValueAtTime(n.volumen, a + (n.ataque ?? 0.01));
      gain.gain.exponentialRampToValueAtTime(0.0001, fin);
      osc.connect(gain).connect(maestro);
      osc.start(a);
      osc.stop(fin + 0.05);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Al primer clic o tecla de la página, crea y reanuda el audio (dentro del gesto, que es cuando el navegador deja):
 * desde ahí los avisos suenan también con la pestaña de fondo. Devuelve cómo dejar de escuchar.
 */
export function desbloquearAudioAlPrimerGesto(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const eventos = ['pointerdown', 'keydown'] as const;
  const quitar = () => {
    for (const ev of eventos) window.removeEventListener(ev, alGesto, true);
  };
  function alGesto() {
    const ac = contextoDeAudio();
    if (!ac) return quitar();
    if (estado(ac) === 'running') return quitar();
    void ac
      .resume()
      .then(() => {
        if (estado(ac) === 'running') quitar();
      })
      .catch(() => undefined);
  }
  for (const ev of eventos) window.addEventListener(ev, alGesto, true);
  return quitar;
}
